<script lang="ts" setup>
import AmazonProduct from "@/components/AmazonProduct.vue";
import AppButton from "@/components/AppButton.vue";
import AppLoadingPlaceholder from "@/components/AppLoadingPlaceholder.vue";
import BaseAlert from "@/components/BaseAlert.vue";
import BaseCard from "@/components/BaseCard.vue";
import BaseCheckbox from "@/components/BaseCheckbox.vue";
import BaseContent from "@/components/BaseContent.vue";
import BaseLabel from "@/components/BaseLabel.vue";
import BasePageHeading from "@/components/BasePageHeading.vue";
import BaseSearchBar from "@/components/BaseSearchBar.vue";
import BaseSelect from "@/components/BaseSelect.vue";
import BaseSidebarSection from "@/components/BaseSidebarSection.vue";
import CharacterThumbnail from "@/components/CharacterThumbnail.vue";
import FilterCategories from "@/components/FilterCategories.vue";
import TheLayout from "@/components/TheLayout.vue";
import TheSidebar from "@/components/TheSidebar.vue";
import { useLog } from "@/composables/log";
import { useNavigator } from "@/composables/navigator";
import { useScroll } from "@/composables/scroll";
import { useTeamBuilder } from "@/composables/team-builder";
import useToast from "@/composables/toast";
import { useUser } from "@/composables/user";
import SelectedCharacters from "@/pages/Team/partials/SelectedCharacters.vue";
import SelectedFilters from "@/pages/Team/partials/SelectedFilters.vue";
import { Category } from "@/types/category";
import { Character } from "@/types/character";
import { SupportMemory } from "@/types/support-memory";
import { User } from "@/types/user";
import { LinkIcon } from "@heroicons/vue/16/solid";
import { Link, router, usePage } from "@inertiajs/vue3";
import {
  breakpointsTailwind,
  useBreakpoints,
  watchDebounced,
} from "@vueuse/core";
import { computed, defineProps, ref, watch } from "vue";
import { route } from "ziggy-js";

const page = usePage();
const user = computed(() => page.props.user);
const auth = useUser(page.props.user);
const log = useLog();
const breakpoints = useBreakpoints(breakpointsTailwind);

const props = defineProps<{
  categories: Category[];
  suggested_characters?: Character[];
  selected_characters?: Character[];
  support_memories?: SupportMemory[];
  box_user?: User;
}>();

const sidebarOpen = ref(false);

//==============================================================================
// State
//==============================================================================

const userBoxOnly = ref<boolean>(!!route().params.box);
const lowerCategoryAffinity = ref<boolean>(
  route().params.category_affinity === "170",
);
const teamBuilder = useTeamBuilder();

teamBuilder.characters.value = props.selected_characters;
teamBuilder.slot.value = Number(route().params.slot) || null;
teamBuilder.rotations.value =
  route()
    .params.rotations?.split(",")
    .map((r: string) => parseInt(r) || null) || [];
teamBuilder.boxUser.value = userBoxOnly.value
  ? parseInt(route().params.box) || user.value?.id
  : null;
teamBuilder.categories.value =
  route().params.categories?.split(",").map(Number) || [];
teamBuilder.categoryAffinity.value = lowerCategoryAffinity.value
  ? 170
  : parseInt(route().params.category_affinity) || 200;
teamBuilder.order.value = route().params.order ?? "score";
teamBuilder.search.value = route().params.search ?? "";

const teamName = ref<string | null>(
  props.box_user ? `Team for ${props.box_user.name}` : null,
);

const selectedCharactersTemplate = ref<HTMLElement | null>(null);
const availableCharactersTemplate = ref<HTMLElement | null>(null);

//==============================================================================
// Actions
//==============================================================================

/**
 * Selects a slot of the team.
 *
 * @param slot
 */
const selectSlot = (slot: number): void => {
  teamBuilder.selectSlot(slot, () =>
    useScroll(availableCharactersTemplate).toTop(),
  );
};

/**
 * Adds a character to the selected team.
 *
 * @param character
 */
const addCharacter = (character: Character): void => {
  teamBuilder.addCharacter(character);
  useScroll(selectedCharactersTemplate).toTop();
};

/**
 * Filters the available characters.
 */
const filter = (): void => {
  teamBuilder.filter();
};

/**
 * Watches the search value for changes and filters the available characters.
 */
watchDebounced(
  () => teamBuilder.search.value,
  () => {
    filter();
  },
  { debounce: 500, maxWait: 1000 },
);

watch(
  () => teamBuilder.categoriesPartial.value,
  () => {
    if (teamBuilder.categories.value.length > 0) {
      filter();
    }
  },
);

/**
 * Watches the lower category affinity value for changes and filters the available characters.
 */
watch(
  () => lowerCategoryAffinity.value,
  () => {
    teamBuilder.categoryAffinity.value = lowerCategoryAffinity.value
      ? 170
      : 200;
  },
);

watch(
  () => userBoxOnly.value,
  () => {
    teamBuilder.boxUser.value = userBoxOnly.value ? user.value?.id : null;
  },
);

/**
 * Filters the available characters by category.
 *
 * @param id
 * @param isSelected
 */
const filterCategories = (id: number, isSelected: boolean): void => {
  teamBuilder.categories.value = isSelected
    ? [...teamBuilder.categories.value, id]
    : teamBuilder.categories.value.filter((c) => c !== id);

  console.log(teamBuilder.categories.value);
  filter();
};

/**
 * Cancels the building for another user's box.
 */
const cancelBoxUser = (): void => {
  teamName.value = null;
  teamBuilder.boxUser.value = user.value?.id;
  userBoxOnly.value = false;
  filter();
};

const showLinkAmount = computed<boolean>(
  () =>
    teamBuilder.characters.value.filter(
      (c, i) => c !== null && teamBuilder.slot.value !== i && i !== 6,
    ).length > 0,
);

/**
 * Determines whether to show the synergy bar.
 *
 * @return Whether to show the synergy bar
 */
const showSynergyBar = computed<boolean>(
  () => teamBuilder.order.value === "score" && showLinkAmount.value,
);

/**
 * Calculates the length of the synergy bar.
 *
 * @param score Synergy score
 *
 * @return Synergy bar length from 10 to 100
 */
const synergyBarLength = (score: number): number => {
  const characterAmountWithoutSelf = teamBuilder.characters.value.filter(
    (c, i) => c !== null && i !== teamBuilder.slot.value,
  ).length;
  console.log(characterAmountWithoutSelf);
  const maxSynergy = characterAmountWithoutSelf * 50;
  const divider = 100 / maxSynergy;

  return Math.ceil(Math.max(10, Math.min(score * divider, 100)));
};

/**
 * Saves the team.
 */
function saveTeam(): void {
  router.post(
    route("teams.store"),
    {
      name: teamName.value,
      characters: teamBuilder.characters.value.map((c: Character) => c?.id),
      rotations: teamBuilder.rotations.value,
    },
    {
      onError: (errors: object) => {
        log.error("An error occurred while saving the team.", {
          errors: errors,
        });

        useToast({
          title: "Oops!",
          text: "An error occurred while saving the team.",
          icon: "error",
        });
      },
    },
  );
}

/**
 * Shares the current team setup's link.
 */
const shareLink = (useUserBox: boolean = false): void => {
  const params: { characters: string; rotations?: string; box?: number } = {
    characters: teamBuilder.characters.value
      .map((c: Character) => c?.id)
      .join(","),
  };

  if (teamBuilder.rotations.value.some((r) => r !== null)) {
    params.rotations = teamBuilder.rotations.value
      .map((r) => (r !== null ? r : ""))
      .join(",");
  }

  if (useUserBox || userBoxOnly.value) {
    params.box = teamBuilder.boxUser.value;
  }

  const url = route(route().current(), params);

  if (useNavigator().available) {
    useNavigator().share(
      "Team Builder | DOKKAN.FYI",
      "Check out my DRAGON BALL Z Dokkan Battle team on DOKKAN.FYI!",
      url,
    );
  } else {
    useNavigator().copy(url, () =>
      useToast({
        title: "Success",
        text: "Link copied to clipboard.",
      }),
    );
  }
};
</script>

<template>
  <TheLayout
    :description="`Interactively create a DRAGON BALL Z Dokkan Battle team${
      userBoxOnly
        ? `, using ${box_user?.name ?? user.name}'s boxed characters,`
        : ''
    } and receive advice about the optimal characters to use.`"
    :heading-href="route('teams.create')"
    heading="Teams"
    title="Dokkan Battle Team Builder"
  >
    <template #navbar>
      <div class="flex grow justify-between gap-4 md:grow-0">
        <!-- Search Bar -->
        <BaseSearchBar
          v-model="teamBuilder.search.value"
          class="w-full"
          placeholder='Search e.g. "AGL Goku"&hellip;'
        />

        <AppButton
          :active="sidebarOpen"
          class="hidden sm:inline-flex"
          icon="filter"
          @click="sidebarOpen = !sidebarOpen"
          >Filters
        </AppButton>
        <AppButton
          :active="sidebarOpen"
          class="sm:hidden"
          @click="sidebarOpen = !sidebarOpen"
        >
          <i-fa6-solid-filter />
        </AppButton>
      </div>
    </template>

    <template #sidebar>
      <TheSidebar v-model:open="sidebarOpen">
        <template #heading> Filters</template>

        <BaseSidebarSection :collapsed="false">
          <BaseCheckbox
            v-if="user && !box_user"
            v-model:checked="userBoxOnly"
            class="whitespace-nowrap"
            @change="filter"
            >My Box Only
          </BaseCheckbox>

          <BaseCheckbox
            v-model:checked="lowerCategoryAffinity"
            class="whitespace-nowrap"
            @change="filter"
            >Include 170%
            {{ teamBuilder.slot.value === 0 ? "Leaders" : "Teammates" }}
          </BaseCheckbox>

          <BaseSelect
            v-model="teamBuilder.order.value"
            :options="{
              released: 'Release Date',
              links:
                teamBuilder.rotations.value[teamBuilder.slot.value] !== null
                  ? 'Link Skills'
                  : 'Team Links',
              score: 'Synergy',
            }"
            horizontal
            label="Order by"
            @change="filter"
          />
        </BaseSidebarSection>

        <BaseSidebarSection heading="Categories">
          <FilterCategories
            v-model:partial="teamBuilder.categoriesPartial.value"
            :categories="categories"
            :selected="teamBuilder.categories.value"
            @category-selected="filterCategories"
          />
        </BaseSidebarSection>
      </TheSidebar>
    </template>

    <div class="flex flex-wrap items-center justify-between gap-4">
      <BasePageHeading class="mr-0">Team Builder</BasePageHeading>
      <AppButton
        v-if="user"
        :href="route('teams.index')"
        class="ml-auto mr-4 sm:mr-0"
        >My Teams
      </AppButton>
    </div>

    <div class="flex flex-col gap-4 lg:flex-row">
      <section
        ref="selectedCharactersTemplate"
        class="xl:4/12 flex shrink-0 flex-col gap-4 lg:w-5/12 xl:w-1/2 2xl:w-5/12"
      >
        <SelectedCharacters
          v-model:team-name="teamName"
          :box-user="box_user"
          :next-slot="teamBuilder.nextSlot.value"
          :selected-characters="teamBuilder.characters.value"
          :selected-rotations="teamBuilder.rotations.value"
          :selected-slot="teamBuilder.slot.value"
          :support-memories="support_memories"
          @slot-selected="selectSlot($event)"
          @rotations-updated="teamBuilder.updateRotations"
          @slot-cleared="teamBuilder.clearSlot"
          @team-saved="saveTeam"
          @link-shared="shareLink"
        />
      </section>

      <section
        ref="availableCharactersTemplate"
        class="flex grow flex-col gap-4"
      >
        <BaseCard v-if="suggested_characters" class="flex w-full flex-col">
          <template #heading>Suggested Characters</template>

          <SelectedFilters
            :box-user="box_user"
            :box-user-id="teamBuilder.boxUser.value"
            :categories="categories"
            :category-affinity="teamBuilder.categoryAffinity.value"
            :is-rotation-slot="teamBuilder.isRotationSlot()"
            :order="teamBuilder.order.value"
            :search="teamBuilder.search.value"
            :selected-categories="teamBuilder.categories.value"
            :selected-slot="teamBuilder.slot.value"
            :user="user"
            class="cursor-pointer"
            @click="sidebarOpen = !sidebarOpen"
            @box-user-canceled="cancelBoxUser"
          />

          <BaseContent v-if="teamBuilder.slot.value === null">
            Select a slot to view suggested characters.
          </BaseContent>

          <template v-else>
            <BaseContent
              v-if="teamBuilder.loading.value"
              class="flex flex-col gap-2 text-center"
            >
              <AppLoadingPlaceholder />
              Searching for characters...
            </BaseContent>

            <div
              v-else-if="
                suggested_characters.filter((c) => c !== null).length === 0
              "
            >
              No suggested characters for the current team composition.
            </div>
            <div v-else class="flex flex-col gap-4">
              <BaseAlert v-if="teamBuilder.isRotationSlot()" type="warning">
                You are selecting a rotation partner for
                <span class="font-extrabold">{{
                  teamBuilder.characters.value[
                    teamBuilder.rotations.value[teamBuilder.slot.value]
                  ].name
                }}</span
                >. The sort order was automatically updated to
                <span class="font-extrabold">Link Skills</span>.
              </BaseAlert>

              <div
                class="grid w-full grid-cols-[repeat(auto-fit,94px)] place-content-around gap-4"
              >
                <div
                  v-for="character in suggested_characters"
                  :key="`suggested_${character.id}`"
                  class="flex flex-col items-center justify-between gap-1"
                >
                  <CharacterThumbnail
                    :key="`suggested_${character.id}`"
                    :character="character"
                    :class="{
                      grayscale: teamBuilder.characters.value.some(
                        (c: Character) =>
                          c?.variant_id === character.variant_id,
                      ),
                    }"
                    :clickable="false"
                    :show-hidden-potential="false"
                    class="cursor-pointer"
                    scale="sm"
                    @click="addCharacter(character)"
                  />

                  <!-- Synergy Bar -->
                  <div
                    v-if="showSynergyBar"
                    class="mx-auto min-h-1.5 w-[75px] text-center font-condensed text-sm"
                  >
                    <div
                      :style="`width: ${synergyBarLength(character.score)}%`"
                      class="h-full overflow-hidden rounded border border-black"
                    >
                      <div
                        class="h-full w-[75px] rounded bg-gradient-to-r from-red-600 via-yellow-500 to-green-400"
                      ></div>
                    </div>
                  </div>

                  <!-- Actions -->
                  <div
                    class="flex w-[75px] items-start justify-between gap-2 text-xs"
                  >
                    <div v-if="showLinkAmount" class="flex items-center">
                      <LinkIcon class="h-4 text-white" />
                      <BaseLabel>{{ character.link_skills_amount }}</BaseLabel>
                    </div>

                    <Link
                      :class="{ 'mx-auto': !showLinkAmount }"
                      :href="route('characters.show', character.id)"
                      prefetch
                      >Details
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </BaseCard>

        <AmazonProduct
          :amount="breakpoints.smallerOrEqual('sm').value ? 1 : 3"
        />
      </section>
    </div>
  </TheLayout>
</template>

<style scoped></style>
